/*
 * Copyright (c) CIRROM 2026.
 */

import { useEffect, useRef, useState } from "react";
import {
  addCompanyAddress,
  AddCompanyAddressResult,
  CompanyAddressOptions,
  EmailAndAddressSuggestion,
  getCompanyAddressOptions,
  getEmailAndAddressSuggestions,
} from "../../../api/cirrommonitor";
import { looksLikeEmail } from "../AdHocEdit";
import { CrmAccount } from "./actions";
import {
  getVerification,
  isEmailWriteBlocked,
  requestEmailVerification,
  useEmailVerificationVersion,
} from "./emailVerification";
import { normalizeValue } from "./normalize";

/*
 * What else a chosen account can tell us about the contact, and which of it
 * the steward has opted to send with the company.
 *
 * The picker owns the account; this owns everything downstream of it - the
 * lookup, the two opt-ins and the rules for when each may ride the write -
 * so the component is left with layout and the rules can be read (and
 * tested) in one place.
 */

/** What else rides with a company write. Both optional, either or both. */
export interface AccountFollowOns {
  email?: string;
  address?: { id: string; description: string };
}

/** Where a work email derived from the account would land. */
export interface AccountEmailTarget {
  label: string;
  currentValue: string;
  /** The contact's own CRM id, recorded on any verification it asks for. */
  crmContactId?: string;
}

export type AccountAddress = EmailAndAddressSuggestion["addresses"][number];

export const describeAddress = (address: AccountAddress): string =>
  [
    address.street,
    address.city,
    address.stateorprovince,
    address.country,
    address.zipcode,
  ]
    .filter(Boolean)
    .join(", ");

export interface EmailFollowOnState {
  /** The account's guess, when there is one worth offering. */
  suggested?: string;
  /** Why there is no email to take, when that needs saying. */
  note?: string;
  /** The steward's text, trimmed: what a check or a write would use. */
  typed: string;
  valid: boolean;
  /** Allegrow has refused this address. */
  blocked: boolean;
  /** Something worth writing: an address, and not the one already held. */
  writable: boolean;
  /** Ticked, and allowed to be: this is what goes with the company. */
  chosen: boolean;
  /** Why the email can't be ticked, when the verdict doesn't already say. */
  reason?: string;
}

/**
 * Everything the email follow-on shows or sends, from what the account knows
 * and what the steward has typed.
 */
export const describeEmailFollowOn = (input: {
  /** False until the lookup has answered; a failed fetch knows nothing. */
  loaded: boolean;
  accountEmail: string;
  accountName: string;
  /** Absent when nothing can receive the email. */
  target?: AccountEmailTarget;
  draft: string;
  included: boolean;
}): EmailFollowOnState => {
  const { loaded, accountEmail, accountName, target, draft, included } = input;
  const held = normalizeValue(target?.currentValue ?? "");
  /*
   * An account email the target field already holds is not news. Neither is
   * one with nowhere to go: showing it would be a suggestion the steward
   * cannot take.
   */
  const alreadyHeld =
    Boolean(target) &&
    Boolean(accountEmail) &&
    normalizeValue(accountEmail) === held;
  const suggested =
    target && accountEmail && !alreadyHeld ? accountEmail : undefined;
  /*
   * Silence here read as a bug - the account was chosen, something clearly
   * loaded, and the email simply never appeared - so the absence says which
   * absence it is.
   */
  const note =
    !loaded || suggested || !target
      ? undefined
      : alreadyHeld
      ? `${target.label} already holds the work email for ${accountName}.`
      : `Couldn't work out a work email at ${accountName}.`;

  /*
   * The guess can be close but wrong - a different format, a nickname - so
   * everything downstream (the check, the verdict, the write) follows what
   * the steward typed rather than the guess.
   */
  const typed = draft.trim();
  const valid = looksLikeEmail(typed);
  const blocked = isEmailWriteBlocked(typed);
  const writable = valid && normalizeValue(typed) !== held;
  /*
   * A verdict that says the mailbox is dead or will bounce takes the email
   * out of the write rather than warning beside it: the CRM write has no
   * undo, and a guessed address Allegrow has already refused is not the
   * steward's call to make. Derived, so a verdict landing after the box was
   * ticked un-ticks it.
   */
  const chosen = included && Boolean(suggested) && writable && !blocked;
  /*
   * Said under the field rather than only in a tooltip: a tick that silently
   * refuses read as broken, and a tooltip on a disabled control is out of
   * reach from the keyboard. A blocked verdict needs no line here - the
   * verdict beneath already says it.
   */
  const reason = !typed
    ? "Enter an email to write it with the company."
    : !valid
    ? `Enter a complete email, like ${suggested ?? "name@firm.com"}.`
    : !writable
    ? `${target?.label} already holds this email, so there is nothing to write.`
    : undefined;

  return { suggested, note, typed, valid, blocked, writable, chosen, reason };
};

/** What the steward types for an office the CRM doesn't hold yet. */
export interface NewAddressValues {
  street: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  addressType: string;
}

/**
 * Whether a new address can be sent, and if not, why - the first blank
 * required field, said under the form as the email row says its reason.
 */
export const describeNewAddressForm = (input: {
  street: string;
  city: string;
  country: string;
}): { valid: boolean; reason?: string } => {
  const reason = !input.street.trim()
    ? "Enter a street."
    : !input.city.trim()
    ? "Enter a city."
    : !input.country.trim()
    ? "Enter a country."
    : undefined;
  return { valid: !reason, reason };
};

const emptyNewAddress = (addressType: string): NewAddressValues => ({
  street: "",
  city: "",
  state: "",
  postcode: "",
  country: "",
  addressType,
});

/* "your CRM" opens a sentence as "Your CRM". */
const sentenceStart = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1);

/**
 * What a failed create says. The form stays open with its values either way;
 * this is only the line under it.
 */
const describeCreateError = (
  result: Extract<AddCompanyAddressResult, { kind: "error" }>,
  accountName: string,
  crmName: string
): string => {
  if (result.status === 422) {
    return typeof result.detail === "string" && result.detail
      ? result.detail
      : "Check the address and try again.";
  }
  if (result.status === 404) {
    return `Couldn't find ${accountName} in Cirrom's latest read.`;
  }
  if (result.status === 403) {
    return "You don't have permission to create addresses.";
  }
  /*
   * Some CRMs create the address and still answer 502, so this never invites
   * a plain retry: that is how a duplicate gets made.
   */
  return `${sentenceStart(
    crmName
  )} didn't confirm the address. Check ${crmName} before trying again, it may have been created.`;
};

export const useAccountFollowOns = (args: {
  account: CrmAccount | undefined;
  integratedSystemId: number;
  recordId: number;
  /** Absent when the email has nowhere to go or no way to be written. */
  emailTarget: AccountEmailTarget | undefined;
  /** Where a new address lands, named: "DealCloud", or "your CRM". */
  crmName: string;
}) => {
  const { account, integratedSystemId, recordId, emailTarget, crmName } = args;
  const [suggestions, setSuggestions] =
    useState<EmailAndAddressSuggestion | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  /*
   * Each follow-on is a tick plus its value. Picking a value ticks it - that
   * is the intent - and un-ticking keeps the value, so a steward can drop a
   * follow-on from the write without losing what they chose or typed.
   */
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [includeAddress, setIncludeAddress] = useState(false);
  /* The account's guess at the work email, editable before it is written. */
  const [emailDraft, setEmailDraft] = useState("");
  const [includeEmail, setIncludeEmail] = useState(false);
  /*
   * Allegrow's verdict is read through plain getters, so this is what
   * re-derives the email's state when a verdict lands.
   */
  useEmailVerificationVersion();

  /*
   * Whether this CRM can take a new company address, asked once per system:
   * the answer is the CRM's, not the account's. A failed ask is a no.
   */
  const [addressOptions, setAddressOptions] = useState<{
    systemId: number;
    options: CompanyAddressOptions | null;
  } | null>(null);
  const optionsAskedFor = useRef<number | null>(null);
  const hasAccount = Boolean(account);
  useEffect(() => {
    if (!hasAccount || !integratedSystemId) return;
    if (optionsAskedFor.current === integratedSystemId) return;
    optionsAskedFor.current = integratedSystemId;
    getCompanyAddressOptions(integratedSystemId)
      .then((options) =>
        setAddressOptions({ systemId: integratedSystemId, options })
      )
      .catch(() =>
        setAddressOptions({ systemId: integratedSystemId, options: null })
      );
  }, [hasAccount, integratedSystemId]);
  const options =
    addressOptions?.systemId === integratedSystemId
      ? addressOptions.options
      : null;
  const addressTypes = options?.address_types ?? [];

  /*
   * The new-address form. The create is the one CRM write here that happens
   * on its own and at once, so while the form is open nothing else in the
   * panel can be changed or written.
   */
  const [formOpen, setFormOpen] = useState(false);
  const [formValues, setFormValues] = useState<NewAddressValues>(
    emptyNewAddress("")
  );
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | undefined>();
  const [createNote, setCreateNote] = useState<string | undefined>();
  /* Bumped on every account change, so a late answer can't land on another. */
  const accountVersion = useRef(0);

  /*
   * Read-only, so it runs on selection rather than waiting for the company
   * write - the steward should see everything the choice implies before
   * committing to it.
   */
  useEffect(() => {
    setSuggestions(null);
    setFailed(false);
    setLoading(false);
    setSelectedAddressId("");
    setIncludeAddress(false);
    setEmailDraft("");
    setIncludeEmail(false);
    accountVersion.current += 1;
    setFormOpen(false);
    setCreating(false);
    setCreateError(undefined);
    setCreateNote(undefined);
    if (!account || !integratedSystemId || !recordId) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    getEmailAndAddressSuggestions(
      integratedSystemId,
      recordId,
      account.crm_account_id
    )
      .then((data) => {
        if (cancelled) return;
        /*
         * An address the CRM holds with every line blank is a row, not an
         * address. Left in, it rendered as an empty option in the picker.
         */
        const usable = (data.addresses ?? []).filter((address) =>
          describeAddress(address)
        );
        /*
         * The follow-ons arrive ticked, so one click writes the whole job
         * move: the suggested email, and the first office when there is one.
         * The steward opts out rather than in. The email's tick still yields
         * to describeEmailFollowOn - nowhere to go, already held or refused
         * by Allegrow keeps it out of the write.
         */
        setSuggestions({ ...data, addresses: usable });
        setEmailDraft(data.suggested_email || "");
        setIncludeEmail(Boolean(data.suggested_email));
        if (usable.length > 0) {
          setSelectedAddressId(usable[0].address_id);
          setIncludeAddress(true);
        }
      })
      .catch(() => {
        // The company link stands without them; nothing else is lost.
        if (!cancelled) {
          setSuggestions(null);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [account, integratedSystemId, recordId]);

  const email = describeEmailFollowOn({
    loaded: Boolean(suggestions),
    accountEmail: suggestions?.suggested_email || "",
    accountName: account?.name ?? "",
    target: emailTarget,
    draft: emailDraft,
    included: includeEmail,
  });

  /*
   * Picking the account is the steward's action that checks its suggested
   * email. Keyed on the suggestion, not the draft, so typing never spends a
   * credit; an address with any entry already is not asked about again.
   */
  const suggestedEmail = email.suggested;
  const crmContactId = emailTarget?.crmContactId;
  useEffect(() => {
    if (
      !suggestedEmail ||
      !looksLikeEmail(suggestedEmail) ||
      getVerification(suggestedEmail)
    ) {
      return;
    }
    requestEmailVerification(suggestedEmail, {
      crmContactId,
      integratedSystemId,
    });
  }, [suggestedEmail, crmContactId, integratedSystemId]);

  const addresses = suggestions?.addresses ?? [];
  const selectedAddress = addresses.find(
    (address) => address.address_id === selectedAddressId
  );
  const chosenAddress = includeAddress ? selectedAddress : undefined;

  const selectAddress = (addressId: string) => {
    setSelectedAddressId(addressId);
    setIncludeAddress(Boolean(addressId));
  };

  const formValidation = describeNewAddressForm(formValues);
  const newAddressAvailable =
    Boolean(suggestions) && !failed && options?.supported === true;

  /** Adds the address as typed, unless the loaded options already hold it. */
  const addAddressFromForm = (id: string) => {
    const added: AccountAddress = {
      address_id: id,
      street: formValues.street.trim(),
      city: formValues.city.trim(),
      stateorprovince: formValues.state.trim(),
      zipcode: formValues.postcode.trim(),
      country: formValues.country.trim(),
    };
    setSuggestions((current) =>
      !current || current.addresses.some((address) => address.address_id === id)
        ? current
        : { ...current, addresses: [...current.addresses, added] }
    );
  };

  /*
   * Only ever called from the confirm dialog. No undo window: the CRM writes
   * the address the moment this is sent.
   */
  const createAddress = async () => {
    if (!account || !formValidation.valid || creating) return;
    const version = accountVersion.current;
    const accountName = account.name;
    setCreating(true);
    setCreateError(undefined);
    const state = formValues.state.trim();
    const postcode = formValues.postcode.trim();
    const result = await addCompanyAddress({
      integrated_system_id: integratedSystemId,
      crm_account_id: account.crm_account_id,
      street: formValues.street.trim(),
      city: formValues.city.trim(),
      ...(state ? { state_or_province: state } : {}),
      ...(postcode ? { zip_code: postcode } : {}),
      country: formValues.country.trim(),
      ...(addressTypes.length > 0 && formValues.addressType
        ? { address_type: formValues.addressType }
        : {}),
    });
    if (version !== accountVersion.current) return;
    setCreating(false);
    if (result.kind === "error") {
      setCreateError(describeCreateError(result, accountName, crmName));
      return;
    }
    /*
     * Selected and ticked, not linked: the link goes with the company
     * through the normal write, after the company has landed.
     */
    addAddressFromForm(result.id);
    selectAddress(result.id);
    setFormOpen(false);
    setCreateNote(
      result.kind === "created"
        ? `Address created in ${crmName}.`
        : `This address already exists on ${accountName}, selected it.`
    );
  };

  /** Exactly what the company write should carry. */
  const chosen: AccountFollowOns = {
    email: email.chosen ? email.typed : undefined,
    address: chosenAddress
      ? {
          id: chosenAddress.address_id,
          description: describeAddress(chosenAddress),
        }
      : undefined,
  };

  return {
    loading,
    failed,
    /** The lookup answered, so "nothing found" can be said as a fact. */
    loaded: Boolean(suggestions),
    email: {
      ...email,
      draft: emailDraft,
      setDraft: (value: string) => {
        setEmailDraft(value);
        setIncludeEmail(true);
      },
      setIncluded: setIncludeEmail,
    },
    office: {
      addresses,
      selectedId: selectedAddressId,
      selected: selectedAddress,
      included: Boolean(chosenAddress),
      select: selectAddress,
      setIncluded: setIncludeAddress,
    },
    newAddress: {
      /** The CRM takes new addresses, and the account's offices loaded. */
      available: newAddressAvailable,
      addressTypes,
      open: formOpen,
      values: formValues,
      setValue: (name: keyof NewAddressValues, value: string) =>
        setFormValues((current) => ({ ...current, [name]: value })),
      valid: formValidation.valid,
      reason: formValidation.reason,
      creating,
      error: createError,
      note: createNote,
      /** Nothing else in the panel may change or write while this holds. */
      locked: formOpen || creating,
      /*
       * Opening leaves the office selection and its tick alone, so
       * dismissing is all it takes to put them back.
       */
      openForm: () => {
        setFormValues(emptyNewAddress(options?.default_type ?? ""));
        setCreateError(undefined);
        setCreateNote(undefined);
        setFormOpen(true);
      },
      dismiss: () => {
        if (creating) return;
        setFormOpen(false);
        setCreateError(undefined);
      },
      create: createAddress,
    },
    chosen,
  };
};

export type AccountFollowOnsModel = ReturnType<typeof useAccountFollowOns>;
